// src/components/DeskFloorMap.jsx — FINAL (mobile height tweak + optional initialZoom)
// Tidak mengubah fitur lain (drag/pinch/±/export)

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

export function DeskFloorPicker({ current, setCurrent, floors }) {
  return (
    <div className="flex flex-wrap gap-2">
      {floors.map((f) => (
        <button
          key={f}
          onClick={() => setCurrent(f)}
          className={`px-3 py-2 rounded-lg text-sm font-medium ${
            current === f ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          {f}
        </button>
      ))}
    </div>
  );
}

/* utils */
const DOT_SIZE = 40;
const GRID = 10;
const pad2 = (n) => String(n).padStart(2, '0');
const suffixNum = (name = '') => {
  const m = String(name).match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : null;
};
const upsertSeat = (arr, seat) => {
  const i = arr.findIndex((s) => s.id === seat.id);
  if (i === -1) return [...arr, seat];
  const copy = arr.slice();
  copy[i] = { ...copy[i], ...seat };
  return copy;
};

function Dot({ x, y, label, taken, selected, editable, size = DOT_SIZE, title }) {
  const half = size / 2;
  const base = taken ? 'fill-red-500' : 'fill-emerald-500';
  const rx = Math.round(size * 0.22);
  const ringStrokeWidth = Math.max(1.5, Math.round(size * 0.09));
  const ring = selected ? 'stroke-white/80' : editable ? 'stroke-white/25' : '';
  const fontSize = Math.max(10, Math.round(size * 0.46));
  return (
    <g>
      {title ? <title>{title}</title> : null}
      <rect x={x - half} y={y - half} width={size} height={size} rx={rx} className={`${base} ${ring}`} style={{ strokeWidth: ringStrokeWidth }} />
      <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fill="#111827" style={{ fontSize, fontWeight: 800, letterSpacing: 0.2 }} pointerEvents="none">
        {label}
      </text>
    </g>
  );
}

export default function DeskFloorMap({
  floorName,
  desks,
  map,
  onPick,
  editable: editableProp,
  onChangeSeats,

  // ⬇️ Baru: kontrol tampilan mobile & zoom awal (opsional)
  mobileVh = 0.6,       // 60% tinggi viewport di mobile (ubah sesukamu, contoh: 0.55)
  mobileMinPx = 300,    // tinggi minimum di mobile
  initialZoom,          // misal 0.9 initialZoom={0.9}     // contoh: mulai di 90% atau 1.2; jika tidak diisi => tidak dipakai
  initialCenter = true  // jika initialZoom diberikan, apakah dipusatkan?
}) {
  const editableByQuery =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('edit') === '1';
  const editable = !!(editableProp ?? editableByQuery);

  const normalized = useMemo(() => {
    if (!map) return { width: 1800, height: 800, walls: [], seats: [], bg: null };
    if (Array.isArray(map)) return { width: 1800, height: 800, walls: [], seats: map, bg: null };
    const width = map.width ?? map.viewBox?.[2] ?? 1800;
    const height = map.height ?? map.viewBox?.[3] ?? 800;
    const walls = map.walls ?? [];
    const seats = map.seats ?? map.slots ?? [];
    const bg = map.bg ?? null;
    return { width, height, walls, seats, bg };
  }, [map]);

  const aspect = normalized.width / normalized.height;

  const [seats, setSeats] = useState(normalized.seats);
  useEffect(() => setSeats(normalized.seats), [normalized.seats]);

  /* keep seats only for existing resources */
  const resourceNames = useMemo(() => new Set((desks || []).map((d) => d.name)), [desks]);
  useEffect(() => {
    setSeats((prev) => {
      const filtered = prev.filter((s) => resourceNames.has(s.id));
      if (filtered.length !== prev.length) onChangeSeats && onChangeSeats(filtered);
      return filtered;
    });
  }, [resourceNames, onChangeSeats]);

  const deskByName = useMemo(() => {
    const m = new Map();
    (desks || []).forEach((r) => m.set(r.name, r));
    return m;
  }, [desks]);

  const seatsWithNew = useMemo(() => {
    const base = (seats || []).filter((s) => resourceNames.has(s.id));
    const have = new Set(base.map((s) => s.id));
    const missing = (desks || [])
      .filter((r) => !have.has(r.name))
      .map((r, i) => {
        const GAP = 44;
        const startX = Math.max(80, normalized.width - 480);
        const startY = 120;
        const cols = 8;
        const x = startX + (i % cols) * GAP;
        const y = startY + Math.floor(i / cols) * GAP;
        return { id: r.name, x, y };
      });
    return [...base, ...missing];
  }, [seats, desks, resourceNames, normalized.width]);

  /* bg anti-flicker */
  const [bgReady, setBgReady] = useState(false);
  const bgUrl = normalized.bg?.url || null;
  useEffect(() => { setBgReady(false); }, [bgUrl, normalized.width, normalized.height]);

  /* ===== mobile-only canvas height (lebih pendek) ===== */
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const [canvasH, setCanvasH] = useState(null); // null => desktop auto

  const measureMobile = useCallback(() => {
    const isMobile = (window.innerWidth || 0) <= 768;
    if (!isMobile) { setCanvasH(null); return; }
    const vh = window.innerHeight || 0;
    // default 60vh dengan min 300px (bisa diubah via props)
    const target = Math.max(mobileMinPx, Math.floor(vh * mobileVh));
    setCanvasH(target);
  }, [mobileMinPx, mobileVh]);

  useEffect(() => {
    measureMobile();
    window.addEventListener('resize', measureMobile);
    window.addEventListener('orientationchange', measureMobile);
    return () => {
      window.removeEventListener('resize', measureMobile);
      window.removeEventListener('orientationchange', measureMobile);
    };
  }, [measureMobile]);

  /* === Interaksi (tidak diubah) === */
  const [zoom, setZoom] = useState(1); // tidak auto-fit
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragId, setDragId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [bgOpacity, setBgOpacity] = useState(normalized.bg?.opacity ?? 1);

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const snap = useCallback((v) => Math.round(v / GRID) * GRID, []);

  const clientToSvg = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const ctm = (viewportRef.current?.getScreenCTM?.() || svg.getScreenCTM()).inverse();
    const cursor = pt.matrixTransform(ctm);
    return { x: cursor.x, y: cursor.y };
  }, []);

  // ⬇️ Opsional: set zoom awal secara manual kalau kamu isi prop initialZoom
  useEffect(() => {
    if (initialZoom == null) return; // tidak dipakai => tidak mengubah perilaku lama
    setZoom(initialZoom);
    if (!initialCenter) return;
    // pusatkan berdasar bounding box svg yang sedang tampil
    requestAnimationFrame(() => {
      const rect = svgRef.current?.getBoundingClientRect?.();
      if (!rect) return;
      const w = rect.width, h = rect.height;
      const panX = (w - normalized.width * initialZoom) / 2;
      const panY = (h - normalized.height * initialZoom) / 2;
      setPan({ x: panX, y: panY });
    });
  }, [initialZoom, initialCenter, normalized.width, normalized.height]);

  const ensureSeatInState = useCallback((id) => {
    if (!resourceNames.has(id)) return;
    const exists = seats.some((s) => s.id === id);
    if (!exists) {
      const fromSynthetic = seatsWithNew.find((s) => s.id === id);
      if (fromSynthetic) {
        setSeats((prev) => {
          const next = [...prev, { id, x: fromSynthetic.x, y: fromSynthetic.y }];
          onChangeSeats && onChangeSeats(next);
          return next;
        });
      }
    }
  }, [seats, seatsWithNew, resourceNames, onChangeSeats]);

  const onMouseDownSeat = (e, id) => {
    if (!editable) return;
    e.preventDefault();
    ensureSeatInState(id);
    setDragId(id);
    setSelected(id);
  };

  const onMoveSeat = (clientX, clientY) => {
    if (!editable || !dragId) return;
    const { x, y } = clientToSvg(clientX, clientY);
    setSeats((prev) => {
      const target = { id: dragId, x: snap(x), y: snap(y) };
      const next = upsertSeat(prev, target);
      onChangeSeats && onChangeSeats(next);
      return next;
    });
  };

  /* ====== gesture pan/pinch ====== */
  const pointers = useRef(new Map());
  const panStart = useRef({ panX: 0, panY: 0, p0: null });
  const pinchStart = useRef(null);

  const isSeatTarget = (t) => t && typeof t.closest === 'function' && t.closest('[data-seat="1"]');

  const pinchMetrics = () => {
    const arr = [...pointers.current.values()];
    if (arr.length < 2) return null;
    const [a, b] = arr;
    const dx = b.clientX - a.clientX;
    const dy = b.clientY - a.clientY;
    const dist = Math.hypot(dx, dy);
    const center = { clientX: (a.clientX + b.clientX) / 2, clientY: (a.clientY + b.clientY) / 2 };
    return { dist, center };
  };

  const startPanAt = (e) => {
    const { clientX, clientY } = e;
    panStart.current = { panX: pan.x, panY: pan.y, p0: clientToSvg(clientX, clientY) };
  };

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const onPointerDown = (e) => {
      if (isSeatTarget(e.target)) return;
      svg.setPointerCapture?.(e.pointerId);
      pointers.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

      if (pointers.current.size === 2) {
        const met = pinchMetrics();
        if (met) {
          const pre = clientToSvg(met.center.clientX, met.center.clientY);
          pinchStart.current = { dist: met.dist, centerPre: pre, zoom0: zoom, pan0: { ...pan } };
        }
      } else if (pointers.current.size === 1) startPanAt(e);
    };

    const onPointerMove = (e) => {
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

      if (dragId) { onMoveSeat(e.clientX, e.clientY); return; }

      if (pointers.current.size >= 2 && pinchStart.current) {
        const met = pinchMetrics();
        if (!met) return;
        const scale = clamp(met.dist / (pinchStart.current.dist || 1), 0.333, 3);
        const z2 = clamp(pinchStart.current.zoom0 * scale, 0.5, 3);
        const pre = pinchStart.current.centerPre;
        setPan({
          x: pinchStart.current.pan0.x + pre.x * (pinchStart.current.zoom0 - z2),
          y: pinchStart.current.pan0.y + pre.y * (pinchStart.current.zoom0 - z2),
        });
        setZoom(z2);
        return;
      }

      if (pointers.current.size === 1 && panStart.current.p0) {
        const p = clientToSvg(e.clientX, e.clientY);
        const dx = (p.x - panStart.current.p0.x) * zoom;
        const dy = (p.y - panStart.current.p0.y) * zoom;
        setPan({ x: panStart.current.panX + dx, y: panStart.current.panY + dy });
      }
    };

    const onPointerUpOrCancel = (e) => {
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2) pinchStart.current = null;
      if (pointers.current.size === 0) panStart.current = { panX: 0, panY: 0, p0: null };
    };

    svg.addEventListener('pointerdown', onPointerDown);
    svg.addEventListener('pointermove', onPointerMove);
    svg.addEventListener('pointerup', onPointerUpOrCancel);
    svg.addEventListener('pointercancel', onPointerUpOrCancel);
    svg.addEventListener('pointerleave', onPointerUpOrCancel);
    return () => {
      svg.removeEventListener('pointerdown', onPointerDown);
      svg.removeEventListener('pointermove', onPointerMove);
      svg.removeEventListener('pointerup', onPointerUpOrCancel);
      svg.removeEventListener('pointercancel', onPointerUpOrCancel);
      svg.removeEventListener('pointerleave', onPointerUpOrCancel);
    };
  }, [zoom, pan, dragId, clientToSvg, onMoveSeat]);

  // wheel zoom (desktop)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e) => {
      e.preventDefault();
      if (isSeatTarget(e.target)) return;
      const { clientX, clientY, deltaY } = e;
      const factor = Math.exp(-deltaY * 0.0015);
      const newZ = clamp(zoom * factor, 0.5, 3);
      if (newZ === zoom) return;
      const p = clientToSvg(clientX, clientY);
      setPan((prev) => ({ x: prev.x + p.x * (zoom - newZ), y: prev.y + p.y * (zoom - newZ) }));
      setZoom(newZ);
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [zoom, clientToSvg]);

  const stepZoom = (dir) => {
    const center = { x: normalized.width / 2, y: normalized.height / 2 };
    const z2 = clamp(zoom + dir * 0.1, 0.5, 3);
    if (z2 === zoom) return;
    setPan((prev) => ({ x: prev.x + center.x * (zoom - z2), y: prev.y + center.y * (zoom - z2) }));
    setZoom(+z2.toFixed(2));
  };

  // mouse/touch edit kursi
  useEffect(() => {
    if (!editable) return;
    const mm = (e) => onMoveSeat(e.clientX, e.clientY);
    const mu = () => setDragId(null);
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', mu);
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);
    };
  }, [editable, onMoveSeat]);
  useEffect(() => {
    if (!editable) return;
    const tm = (e) => {
      if (!dragId) return;
      const t = e.touches[0];
      if (t) onMoveSeat(t.clientX, t.clientY);
    };
    const te = () => setDragId(null);
    window.addEventListener('touchmove', tm, { passive: false });
    window.addEventListener('touchend', te);
    return () => {
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', te);
    };
  }, [editable, dragId, onMoveSeat]);

  return (
    <div className="w-full bg-gray-900 rounded-xl border border-gray-700 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-gray-200 font-semibold">{floorName}</div>

        <div className="flex items-center gap-2">
          <button onClick={() => stepZoom(-1)} className="px-2 py-1 text-sm bg-gray-800 rounded hover:bg-gray-700" aria-label="Zoom out">−</button>
          <input type="range" min="0.5" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(clamp(+e.target.value, 0.5, 3))} aria-label="Zoom" />
          <button onClick={() => stepZoom(1)} className="px-2 py-1 text-sm bg-gray-800 rounded hover:bg-gray-700" aria-label="Zoom in">+</button>

          {editable && normalized.bg?.url && (
            <div className="flex items-center gap-2 ml-3 text-xs text-gray-300">
              BG
              <input type="range" min="0" max="1" step="0.05" value={bgOpacity} onChange={(e) => setBgOpacity(+e.target.value)} title="Opacity background" />
            </div>
          )}

          {editable && (
            <button
              onClick={() => {
                const payload = {
                  width: normalized.width,
                  height: normalized.height,
                  seats: seatsWithNew.map(({ id, x, y }) => ({ id, x, y })),
                };
                const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${floorName.replace(/\s+/g, '_')}_seats.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="ml-3 px-3 py-1.5 text-xs font-medium rounded bg-emerald-600 hover:bg-emerald-500 text-white"
              title="Unduh file seats"
            >
              Export seats
            </button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${normalized.width} ${normalized.height}`}
          className="w-full h-auto select-none"
          role="img"
          aria-label={`Floor map ${floorName}`}
          style={{
            touchAction: 'none',
            // MOBILE ONLY: isi ruang kosong tapi tidak kelewat tinggi
            height: canvasH ? `${canvasH}px` : undefined,
            aspectRatio: `${aspect} / 1`,
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {editable && (
            <>
              <defs>
                <pattern id="grid" width={20} height={20} patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#2d3748" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width={normalized.width} height={normalized.height} fill="url(#grid)" opacity="0.35" />
            </>
          )}

          <g ref={viewportRef} transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
            {normalized.bg?.url && (
              <>
                {!bgReady && (
                  <rect x={0} y={0} width={normalized.bg?.width ?? normalized.width} height={normalized.bg?.height ?? normalized.height} fill="#0f172a" />
                )}
                <image
                  key={normalized.bg.url}
                  href={normalized.bg.url}
                  x={normalized.bg?.x ?? 0}
                  y={normalized.bg?.y ?? 0}
                  width={normalized.bg?.width ?? normalized.width}
                  height={normalized.bg?.height ?? normalized.height}
                  opacity={bgOpacity}
                  preserveAspectRatio="none"
                  style={{ display: bgReady ? 'block' : 'none' }}
                  onLoad={() => setBgReady(true)}
                />
              </>
            )}

            <g>
              {seatsWithNew.map((s) => {
                const res = deskByName.get(s.id);
                if (!res) return null;
                const n = suffixNum(res.name);
                const label = n != null ? pad2(n) : '--';
                const taken = !!res.is_booked;
                const canPick = !editable;
                const bookedBy = res.booked_by || res.bookedBy || res.username || '';
                const bookedInfo = taken ? (bookedBy ? ` - booked by ${bookedBy}` : ' - booked') : '';
                const title = `${res.name} - ${label}${bookedInfo}`;

                return (
                  <g
                    key={s.id}
                    data-seat="1"
                    className={editable ? 'cursor-move' : canPick ? 'cursor-pointer' : 'cursor-default'}
                    onMouseDown={(e) => onMouseDownSeat(e, s.id)}
                    onTouchStart={() => {
                      if (!editable) return;
                      ensureSeatInState(s.id);
                      setDragId(s.id);
                      setSelected(s.id);
                    }}
                    onClick={() => canPick && onPick && onPick({ ...res, displayLabel: label })}
                  >
                    <Dot x={s.x} y={s.y} label={label} title={title} taken={taken} selected={s.id === selected} editable={editable} size={DOT_SIZE} />
                  </g>
                );
              })}
            </g>
          </g>
        </svg>
      </div>

      <div className="mt-3 flex items-center gap-4 text-sm">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" /> Available
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm bg-red-500" /> Booked
        </span>
        {editable && <span className="text-xs text-gray-400">Tip: drag kotak (snap 20px), grid aktif untuk posisi simetris.</span>}
      </div>
    </div>
  );
}


